import { Button, Empty, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import tw from 'twin.macro';

import { PATH } from '@/Route';
import Tescriptions from '@/components/Tescriptions';
import Table from '@/components/list/Table';
import { USER_BRANCH_STATUS, USER_ROLE_TYPE } from '@/constants';
import { useAdminUser } from '@/hooks/queries/admin';

import RolePermissionModal from './components/RolePermissionModal';
import {
  BranchAccessLabel,
  UserStatusBadge,
  UserTypeTag,
} from './components/UserBadges';

const formatDateTime = (value: string | null) =>
  value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-';

const UserDetail = () => {
  const params = useParams();
  const id = Number(params.id);
  const query = useAdminUser({ id }, { enabled: !!id });
  const [permissionModal, setPermissionModal] = useState(false);

  if (query.isError) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="유저 정보를 불러오지 못했어요"
      />
    );
  }

  if (!query.data) {
    return null;
  }

  const user = query.data;
  const activeBranchCount = user.branches.filter(
    (branch) => branch.status === 'ACTIVE'
  ).length;

  return (
    <div css={tw`flex flex-col gap-[48px]`}>
      <Tescriptions title="기본 정보" column={2}>
        <Tescriptions.Item label="이름">{user.name}</Tescriptions.Item>
        <Tescriptions.Item label="유저 ID">{user.id}</Tescriptions.Item>
        <Tescriptions.Item label="이메일">
          <Typography.Text copyable>{user.email}</Typography.Text>
        </Tescriptions.Item>
        <Tescriptions.Item label="휴대폰 번호">{user.phone}</Tescriptions.Item>
        <Tescriptions.Item label="구분">
          <UserTypeTag type={user.userType} />
        </Tescriptions.Item>
        <Tescriptions.Item label="상태">
          <UserStatusBadge status={user.userStatus} />
        </Tescriptions.Item>
        <Tescriptions.Item label="마지막 로그인">
          {formatDateTime(user.lastLogin)}
        </Tescriptions.Item>
        <Tescriptions.Item label="생성일">
          {formatDateTime(user.createdAt)}
        </Tescriptions.Item>
        <Tescriptions.Item label="수정일" span={2}>
          {formatDateTime(user.updatedAt)}
        </Tescriptions.Item>
      </Tescriptions>

      <Tescriptions title="역할" column={2}>
        <Tescriptions.Item label="역할명">
          {user.userRole?.name ?? '-'}
        </Tescriptions.Item>
        <Tescriptions.Item label="역할 유형">
          {user.userRole
            ? (USER_ROLE_TYPE[user.userRole.type] ?? user.userRole.type)
            : '-'}
        </Tescriptions.Item>
        <Tescriptions.Item label="역할 설명" span={2}>
          {user.userRole?.description || '-'}
        </Tescriptions.Item>
      </Tescriptions>

      <Tescriptions title="할당된 권한" column={1}>
        <Tescriptions.Item label="권한 범위">
          <div
            css={tw`flex flex-row items-center justify-between gap-[12px] w-full`}
          >
            {user.hasAllPermissions ? (
              <div css={tw`flex flex-row items-center gap-[8px]`}>
                <Tag color="gold" css={tw`m-0`}>
                  전부
                </Tag>
                <Typography.Text type="secondary">
                  {user.userRole?.isSuperRole
                    ? '최고 관리자 역할이라 매핑과 무관하게 모든 메뉴 · API 권한을 가져요'
                    : '등록된 모든 권한이 역할에 매핑돼 있어요'}
                </Typography.Text>
              </div>
            ) : (
              <Typography.Text>{user.permissions.length}개</Typography.Text>
            )}

            <Button
              disabled={!user.userRole}
              onClick={() => setPermissionModal(true)}
            >
              {user.userRole?.isSuperRole ? '권한 목록' : '권한 목록 · 수정'}
            </Button>
          </div>
        </Tescriptions.Item>
      </Tescriptions>

      {user.userRole && (
        <RolePermissionModal
          isOpen={permissionModal}
          onClose={() => setPermissionModal(false)}
          role={user.userRole}
          permissions={user.permissions}
        />
      )}

      <div css={tw`flex flex-col gap-[16px]`}>
        <Tescriptions title="지점 권한" column={1}>
          <Tescriptions.Item label="접근 지점">
            <div css={tw`flex flex-row items-center gap-[8px]`}>
              <BranchAccessLabel
                hasAllBranchAccess={user.hasAllBranchAccess}
                branchCount={activeBranchCount}
              />
              <Typography.Text type="secondary">
                {user.hasAllBranchAccess
                  ? '지점 매핑과 무관하게 전체 지점에 접근해요'
                  : '아래 활성 매핑 지점에만 접근해요'}
              </Typography.Text>
            </div>
          </Tescriptions.Item>
        </Tescriptions>

        <Table
          headerConfig={{ title: '지점 매핑' }}
          columns={[
            {
              dataIndex: 'branchName',
              title: '지점명',
              width: 'auto',
              render: (value: string | null) => value ?? '-',
            },
            {
              dataIndex: 'branchId',
              title: '지점 ID',
              width: 120,
            },
            {
              dataIndex: 'status',
              title: '상태',
              width: 100,
              render: (_, { status }) => (
                <Tag
                  css={tw`m-0`}
                  color={status === 'ACTIVE' ? 'green' : 'default'}
                >
                  {USER_BRANCH_STATUS[status] ?? status}
                </Tag>
              ),
            },
          ]}
          dataSource={user.branches}
          emptyText={
            user.hasAllBranchAccess
              ? '매핑된 지점이 없어요 (전체 지점 접근)'
              : '매핑된 지점이 없어요'
          }
          rowKey={(row) => row.id}
        />
      </div>
    </div>
  );
};

UserDetail.titleExtra = () => {
  const navigate = useNavigate();

  return (
    <Button onClick={() => navigate(PATH.USER_MANAGE.INDEX)}>목록으로</Button>
  );
};

export default UserDetail;
